import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'; // Đã thêm useNavigate
import { Box, Grid, Paper, Typography, CircularProgress, IconButton, Tooltip, Button } from '@mui/material'; // Đã thêm Button
import Iconify from 'src/components/iconify';
import { useLoSam } from 'src/sections/losam/hooks/use-losam';
import { CaySam } from 'src/types/caysam';
import { LoSam } from 'src/types/losam';
// eslint-disable-next-line import/no-named-as-default
import caySamApiService from 'src/api/caysam-api';
import { LoSamPosition } from '../hooks/use-tracu';
import LotGridMini from '../components/lot-grid-mini';

export const CameraToanCanh = () => {
  const navigate = useNavigate(); // Hook điều hướng
  const location = useLocation();
  const { vuongSamId, loSamId, camera, cameraId, streamUri, cameraState } = location.state || {};
  
  const { getLoSamById, loaiTuoiOptions, diemSucKhoeOptions } = useLoSam();
  const [selectedLot, setSelectedLot] = useState<LoSam | null>(null);
  const [caySams, setCaySams] = useState<CaySam[]>([]);
  const [gridPositions, setGridPositions] = useState<LoSamPosition[][]>([]);
  const [gridSize, setGridSize] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(false);
  const [caySamsLoading, setCaySamsLoading] = useState(false);

  // ===== CẤU HÌNH RADAR CAMERA ===== 
  const [camConfig, setCamConfig] = useState({
    angle: cameraState?.angle || 0,      // Góc quay ngang (trái/phải) - Thêm check null an toàn
    range: cameraState?.range || 50,     // Tầm xa của góc nhìn (lên/xuống)
    spread: cameraState?.spread || 60    // Độ rộng góc nhìn (zoom)
  }); 

  const svgRef = useRef<SVGSVGElement>(null);
  const [svgDimensions, setSvgDimensions] = useState({ width: 0, height: 0 });
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsInstanceRef = useRef<any>(null);

  const hlsUrl = streamUri || '';

  // Load LoSam data
  const loadLoSam = useCallback(async () => {
    if (!loSamId) return;
    
    try {
      setLoading(true);
      const response = await getLoSamById(loSamId);
      if (response.messCode === 1 && response.oneItem) {
        setSelectedLot(response.oneItem as LoSam);
        setCaySams(response.oneItem.caySams || []);
      }
    } catch (error) {
      console.error('Error loading LoSam:', error);
    } finally {
      setLoading(false);
    }
  }, [loSamId, getLoSamById]);

  useEffect(() => {
    loadLoSam();
  }, [loadLoSam]);

  // ===== KHỞI TẠO HLS PLAYER (tránh re-render) =====
  useEffect(() => {
    if (!hlsUrl || !videoRef.current) return;

    const video = videoRef.current;

    // Kiểm tra nếu đã có instance thì destroy trước
    if (hlsInstanceRef.current) {
      hlsInstanceRef.current.destroy();
    }

    // Sử dụng HLS.js nếu browser hỗ trợ
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line import/no-extraneous-dependencies
      import('hls.js').then(({ default: Hls }) => {
        if (Hls.isSupported()) {
          const hls = new Hls({
            enableWorker: true,
            lowLatencyMode: true,
            backBufferLength: 30,
            maxBufferLength: 60,
            maxMaxBufferLength: 120,
            liveSyncDurationCount: 3,
            liveMaxLatencyDurationCount: 10,
            maxLoadingDelay: 4,
            minAutoBitrate: 0,
            startLevel: -1,
            abrEwmaDefaultEstimate: 500000,
            fragLoadingTimeOut: 20000,
            manifestLoadingTimeOut: 10000,
            levelLoadingTimeOut: 10000,
          });

          hls.loadSource(hlsUrl);
          hls.attachMedia(video);

          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            // Không auto play, user phải click
            video.play().catch(() => {
              console.log('Autoplay prevented, user must interact first');
            });
          });

          hls.on(Hls.Events.ERROR, (event, data) => {
            if (data.fatal) {
              switch (data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                  console.error('Network error, trying to recover...');
                  hls.startLoad();
                  break;
                case Hls.ErrorTypes.MEDIA_ERROR:
                  console.error('Media error, trying to recover...');
                  hls.recoverMediaError();
                  break;
                default:
                  console.error('Fatal error, cannot recover');
                  hls.destroy();
                  break;
              }
            }
          });

          hlsInstanceRef.current = hls;
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          // Native HLS support (Safari)
          video.src = hlsUrl;
          video.addEventListener('loadedmetadata', () => {
            video.play().catch(() => {
              console.log('Autoplay prevented');
            });
          });
        }
      });
    }

    // Cleanup
    // eslint-disable-next-line consistent-return
    return () => {
      if (hlsInstanceRef.current) {
        hlsInstanceRef.current.destroy();
        hlsInstanceRef.current = null;
      }
    };
  }, [hlsUrl]);

  // ===== CẬP NHẬT KÍCH THƯỚC SVG =====
  useEffect(() => {
    const updateDimensions = () => {
      if (svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        setSvgDimensions({ width: rect.width, height: rect.height });
      }
    };

    updateDimensions();
    const interval = setInterval(updateDimensions, 500);
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  // ===== VẼ NÓN GÓC NHÌN CAMERA =====
  const drawCone = useCallback(() => {
    const { width, height } = svgDimensions;
    if (width === 0 || height === 0) return '';

    const cx = width / 2;
    const cy = height / 2;
    const { angle, range, spread } = camConfig;
    
    const start = (angle - spread / 2) * Math.PI / 180;
    const end = (angle + spread / 2) * Math.PI / 180;
    
    const pts: [number, number][] = [[cx, cy]];
    
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i <= 40; i++) {
      const t = start + (i / 40) * (end - start);
      pts.push([cx + range * Math.sin(t), cy - range * Math.cos(t)]);
    }
    
    return pts.map(p => p.join(",")).join(" ");
  }, [camConfig, svgDimensions]);

  // ===== ĐIỀU KHIỂN PTZ =====
 const handlePTZ = useCallback(async (action: string) => {
    if (!camera) {
      console.error('Camera information is missing');
      return;
    }

    try {
      // Chuẩn bị payload để gửi đến API
      const payload = {
        id: camera.id || cameraId,
        loSamId,
        loSamLoaiCameraId: camera.loSamLoaiCameraId || camera.loSamLoaiCamera_ID,
        IpCamera: camera.ipCamera,
        rtsp: camera.rtsp,
        onvifCamera: camera.onvifCamera,
        userName: camera.userName,
        password: camera.password,
        trangThai: camera.trangThai || 0,
        action
      }; 
      console.log({"payload":payload})
      const data = await caySamApiService.moveCamera(payload);
      
      console.log(`PTZ ${action} →`, data);

      // Cập nhật lại angle, range, spread từ response của API
      if (data.angle !== undefined || data.range !== undefined || data.spread !== undefined) {
        setCamConfig(prev => ({
          angle: data.angle !== undefined ? data.angle : prev.angle,
          range: data.range !== undefined ? data.range : prev.range,
          spread: data.spread !== undefined ? data.spread : prev.spread
        }));
      }
    } catch (err) {
      console.error('PTZ error:', err);
    }
  }, [camera, cameraId, loSamId]);

  // ===== HIỆU ÚNG PULSE CHO NÓN =====
  const [pulse, setPulse] = useState(0.25);
  useEffect(() => {
    let currentPulse = 0.25;
    let direction = 1;
    
    const interval = setInterval(() => {
      currentPulse += direction * 0.02;
      if (currentPulse > 0.5 || currentPulse < 0.2) direction *= -1;
      setPulse(currentPulse);
    }, 100);
    
    return () => clearInterval(interval);
  }, []);

  // Initialize grid positions
  useEffect(() => {
    if (!selectedLot) return;

    const cols = selectedLot.soCot || 0;
    const rows = selectedLot.soHang || 0;
    const totalCols = cols + 1;
    const middleColumn = Math.floor(totalCols / 2);
    
    setGridSize({ x: totalCols, y: rows });

    const positions: LoSamPosition[][] = Array.from({ length: rows }, (_, y) =>
      // eslint-disable-next-line @typescript-eslint/no-shadow
      Array.from({ length: totalCols }, (_, x) => ({
        x,
        y,
        selected: false,
        occupied: false,
        lotId: selectedLot.loSam_ID!,
      }))
    );

    const parseViTriTrongLo = (viTri: string) => {
      const match = viTri?.match?.(/([A-Z])(\d+)/);
      if (match) {
        const x = match[1].charCodeAt(0) - 65;
        const y = parseInt(match[2], 10) - 1;
        return { x, y };
      }
      return null;
    };

    caySams.forEach((caySam) => {
      const parsed = parseViTriTrongLo(caySam.viTriTrongLo || '');
      if (parsed) {
        // eslint-disable-next-line prefer-const
        let { x, y } = parsed;
        if (x >= middleColumn) x += 1;
        
        if (y >= 0 && y < rows && x >= 0 && x < totalCols) {
          positions[y][x] = {
            x,
            y,
            selected: false,
            occupied: true,
            lotId: selectedLot.loSam_ID!,
            caySam,
          };
        }
      }
    });

    setGridPositions(positions);
  }, [selectedLot, caySams]);

  const getGinsengInfo = useCallback((x: number, y: number) => {
    const position = gridPositions[y]?.[x];
    if (!position?.caySam) return null;

    const latestNhatKy = position.caySam.caySamNhatKys && position.caySam.caySamNhatKys.length > 0
      ? position.caySam.caySamNhatKys[position.caySam.caySamNhatKys.length - 1]
      : null;

    return {
      maCaySam: position.caySam.maCaySam,
      tuoiCay_ID: position.caySam.tuoiCay_ID,
      tinhTrang: latestNhatKy?.tinhTrang || 0,
      diemSucKhoe: latestNhatKy?.diemSucKhoe || 0,
    };
  }, [gridPositions]);

  const handleCellClick = useCallback((x: number, y: number) => {
    console.log('Cell clicked:', x, y);
  }, []);

  return (
    <Box sx={{ p: 3 }}>
        
      {/* ===== HEADER VỚI NÚT BACK ===== */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <Button
          color="inherit"
          startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
          onClick={() => navigate(-1)}
          sx={{ mr: 2 }}
        >
          Quay lại
        </Button>
        <Typography variant="h4">
          Camera toàn cảnh
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Bên trái - Video Camera */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Iconify icon="solar:camera-bold" width={24} />
              <Typography variant="h6">
                {camera?.loSamLoaiCamera?.ten || 'Camera'}
              </Typography>
            </Box>

            {/* Video Player */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                paddingTop: '56.25%',
                backgroundColor: '#000',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              {camera ? (
                <video
                  ref={videoRef}
                  // controls
                  autoPlay
                  muted
                  playsInline
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    color: 'white',
                  }}
                >
                  <Typography>Không có camera</Typography>
                </Box>
              )}
            </Box>

            {/* ===== ĐIỀU KHIỂN PTZ ===== */}
            <Box sx={{ 
              mt: 2,
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1, 
              justifyContent: 'center',
              p: 2,
              backgroundColor: 'rgba(0,0,0,0.05)',
              borderRadius: 1
            }}>
              <Tooltip title="Quay trái">
                <IconButton 
                  onClick={() => handlePTZ('left')} 
                  size="small" 
                  sx={{ bgcolor: 'white', '&:hover': { bgcolor: 'grey.200' } }}
                >
                  ⟲
                </IconButton>
              </Tooltip>
              <Tooltip title="Quay phải">
                <IconButton 
                  onClick={() => handlePTZ('right')} 
                  size="small" 
                  sx={{ bgcolor: 'white', '&:hover': { bgcolor: 'grey.200' } }}
                >
                  ⟳
                </IconButton>
              </Tooltip>
              <Tooltip title="Ngẩng lên (xa hơn)">
                <IconButton 
                  onClick={() => handlePTZ('up')} 
                  size="small" 
                  sx={{ bgcolor: 'white', '&:hover': { bgcolor: 'grey.200' } }}
                >
                  ⬆️
                </IconButton>
              </Tooltip>
              <Tooltip title="Cúi xuống (gần hơn)">
                <IconButton 
                  onClick={() => handlePTZ('down')} 
                  size="small" 
                  sx={{ bgcolor: 'white', '&:hover': { bgcolor: 'grey.200' } }}
                > 
                  ⬇️
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom in">
                <IconButton 
                  onClick={() => handlePTZ('zoomIn')} 
                  size="small" 
                  sx={{ bgcolor: 'white', '&:hover': { bgcolor: 'grey.200' } }}
                >
                  🔍+
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom out">
                <IconButton 
                  onClick={() => handlePTZ('zoomOut')} 
                  size="small" 
                  sx={{ bgcolor: 'white', '&:hover': { bgcolor: 'grey.200' } }}
                >
                  🔍−
                </IconButton>
              </Tooltip>
              <Tooltip title="Reset vị trí">
                <IconButton 
                  onClick={() => handlePTZ('reset')} 
                  size="small" 
                  sx={{ bgcolor: 'white', '&:hover': { bgcolor: 'grey.200' } }}
                >
                  🔄
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
        </Grid>

        {/* ===== BÊN PHẢI - LOTGRID VỚI RADAR OVERLAY ===== */}
        <Grid item xs={12} md={5}>
          <Paper sx={{  minHeight: 400, position: 'relative' }}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
              </Box>
            ) : selectedLot && gridPositions.length > 0 ? (
              <Box sx={{ position: 'relative' }}>
                {/* LotGrid Component */}
                <LotGridMini
                  lotName={selectedLot.tenLo || ''}
                  selectedLot={selectedLot}
                  gridPositions={gridPositions}
                  gridSize={gridSize}
                  caySamsLoading={caySamsLoading}
                  onCellClick={handleCellClick}
                  getGinsengInfo={getGinsengInfo}
                  highlightStatus={0}
                  highlightHealth={0}
                  highlightAge={0}
                  excludeLoggedThisMonth={false}
                  loaiTuoiOptions={loaiTuoiOptions}
                  diemSucKhoeOptions={diemSucKhoeOptions}
                />

                {/* ===== RADAR OVERLAY Ở GIỮA GRID ===== */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '52%',
                    transform: 'translate(-50%, -50%)',
                    width: '80px',
                    height: '80%',
                    pointerEvents: 'none',
                    zIndex: 10,
                  }}
                >
                 
                  {/* SVG cho góc nhìn */}
                  <svg
                    ref={svgRef}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      overflow: 'visible',
                    }}
                  >
                    <polygon 
                      points={drawCone()}
                      fill={`rgba(0, 170, 255, ${pulse})`}
                      stroke="#00aaff"
                      strokeWidth="1.5"
                    />
                  </svg>

                  {/* Camera dot */} 
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '52%',
                      transform: 'translate(-50%, -50%)',
                      width: 25,
                      height: 25,
                      background: 'radial-gradient(circle at center, red 30%, darkred 70%)',
                      borderRadius: '50%',
                      boxShadow: '0 0 8px rgba(255,0,0,0.6)',
                      zIndex: 2,
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <Typography variant="body2" color="text.secondary">
                  Không có dữ liệu lô sâm
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {!location.state && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: 'error.lighter',
            borderRadius: 1,
            color: 'error.dark',
          }}
        >
          <Typography>
            ⚠️ Không nhận được thông tin camera! Vui lòng chọn camera từ danh sách.
          </Typography>
        </Box>
      )}
    </Box>
  );
};